import styled, { css, keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

// ============================================================================
// ANIMATIONS
// ============================================================================

const fadeUp = keyframes`
	from { opacity: 0; transform: translateY(18px); }
	to { opacity: 1; transform: translateY(0); }
`;

const pulseGlow = keyframes`
	0%, 100% { opacity: 0.45; transform: scale(1); }
	50% { opacity: 0.75; transform: scale(1.04); }
`;

const floatOrb = keyframes`
	0%, 100% { transform: translate(0, 0); }
	50% { transform: translate(12px, -8px); }
`;

const shimmerLine = keyframes`
	0% { background-position: 200% center; }
	100% { background-position: -200% center; }
`;

// ============================================================================
// MODAL OVERLAY & SHELL
// ============================================================================

export const PlanModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: ${(props) => (props.$secured ? 7000 : 34)};
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 20px;
	background: rgba(2, 1, 6, 0.88);
	backdrop-filter: blur(18px) saturate(1.15);
	pointer-events: auto;
	overflow: hidden;

	&::before,
	&::after {
		content: '';
		position: absolute;
		border-radius: 50%;
		filter: blur(80px);
		pointer-events: none;
		animation: ${floatOrb} 8s ease-in-out infinite;
	}

	&::before {
		width: 420px;
		height: 420px;
		top: -120px;
		left: -80px;
		background: rgba(176, 38, 255, 0.28);
	}

	&::after {
		width: 360px;
		height: 360px;
		right: -60px;
		bottom: -100px;
		background: rgba(255, 77, 242, 0.2);
		animation-delay: -4s;
	}

	@media (max-width: 1024px) {
		padding: 0;
		align-items: flex-start;
		justify-content: flex-start;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		overscroll-behavior: contain;
	}
`;

export const PlanModalShell = styled.div`
	position: relative;
	z-index: 1;
	width: min(1040px, 100%);
	max-width: 100%;
	min-width: 0;
	height: auto;
	max-height: min(calc(100dvh - 40px), calc(100vh - 40px), 860px);
	overflow-x: hidden;
	overflow-y: auto;
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1.08fr);
	align-items: start;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 26px;
	box-sizing: border-box;
	box-shadow:
		0 48px 140px rgba(0, 0, 0, 0.8),
		0 0 0 1px rgba(255, 77, 242, 0.08) inset,
		0 0 80px rgba(176, 38, 255, 0.15);
	animation: ${fadeUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1);
	-webkit-overflow-scrolling: touch;
	overscroll-behavior: contain;

	@media (max-width: 1024px) {
		width: 100%;
		height: auto;
		min-height: 100dvh;
		max-height: none;
		overflow: visible;
		grid-template-columns: minmax(0, 1fr);
		border: none;
		border-radius: 0;
		box-shadow: none;
	}
`;

// ============================================================================
// MODAL BACKGROUND & DECORATIONS
// ============================================================================

export const ModalBackground = styled.div`
	position: absolute;
	inset: 0;
	z-index: 0;
	pointer-events: none;
	overflow: hidden;

	img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: 22% 6%;
		transform: scale(1.02);
		filter: saturate(1.12) brightness(1.02);
	}

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background:
			linear-gradient(
				90deg,
				rgba(6, 3, 10, 0.08) 0%,
				rgba(6, 3, 10, 0.28) 34%,
				rgba(8, 4, 14, 0.82) 52%,
				rgba(10, 5, 16, 0.97) 68%,
				rgba(10, 5, 16, 0.99) 100%
			),
			linear-gradient(180deg, transparent 55%, rgba(4, 2, 8, 0.55) 100%);
	}
`;

export const BackgroundBadge = styled.div`
	position: absolute;
	left: 32px;
	bottom: 36px;
	z-index: 1;
	pointer-events: none;
	white-space: nowrap;
	font-family: ${THEME.fontDisplay};
	font-size: clamp(1.55rem, 2.8vw, 2.1rem);
	font-weight: 800;
	line-height: 1.04;
	color: ${THEME.gold};
	text-shadow:
		0 2px 28px rgba(0, 0, 0, 0.9),
		0 0 24px rgba(255, 211, 107, 0.35);

	@media (max-width: 1024px) {
		left: max(20px, env(safe-area-inset-left, 0px));
		bottom: auto;
		top: max(20px, env(safe-area-inset-top, 0px));
		font-size: 1.35rem;
	}
`;

export const PlanModalClose = styled.button`
	position: absolute;
	top: 18px;
	right: 18px;
	z-index: 5;
	width: 40px;
	height: 40px;
	border: 1px solid rgba(255, 255, 255, 0.16);
	border-radius: 50%;
	background: rgba(8, 4, 12, 0.72);
	color: rgba(255, 247, 255, 0.92);
	font-size: 1.4rem;
	line-height: 1;
	cursor: pointer;
	backdrop-filter: blur(8px);
	transition:
		border-color 0.2s ease,
		transform 0.2s ease,
		box-shadow 0.2s ease;

	@media (max-width: 1024px) {
		top: max(14px, env(safe-area-inset-top, 0px));
		right: max(14px, env(safe-area-inset-right, 0px));
	}

	&:hover {
		border-color: rgba(255, 77, 242, 0.55);
		box-shadow: 0 0 20px rgba(255, 77, 242, 0.25);
		transform: scale(1.05);
	}
`;

// ============================================================================
// PLANS PANEL (LEFT SIDE)
// ============================================================================

export const PlansPanel = styled.section`
	position: relative;
	z-index: 2;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-start;
	min-width: 0;
	padding: 32px 28px 28px;

	@media (max-width: 1024px) {
		align-items: stretch;
		padding:
			calc(68px + env(safe-area-inset-top, 0px))
			max(20px, env(safe-area-inset-right, 0px))
			20px
			max(20px, env(safe-area-inset-left, 0px));
	}
`;

export const PlansContent = styled.div`
	width: 100%;
	max-width: 400px;
	flex-shrink: 0;

	@media (max-width: 1024px) {
		max-width: 100%;
	}
`;

export const PanelTitle = styled.h2`
	margin: 0 0 6px;
	font-family: ${THEME.fontDisplay};
	font-size: 1.45rem;
	line-height: 1.12;
	color: ${THEME.text};
	text-shadow: 0 2px 16px rgba(0, 0, 0, 0.5);
`;

export const PanelSubtitle = styled.p`
	margin: 0 0 14px;
	color: rgba(216, 189, 228, 0.92);
	font-size: 0.84rem;
	line-height: 1.5;
	text-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
`;

export const PlanBenefits = styled.ul`
	margin: 0 0 18px;
	padding: 0;
	list-style: none;
	display: grid;
	gap: 8px;
	width: 100%;
`;

export const PlanBenefitItem = styled.li`
	display: flex;
	align-items: flex-start;
	gap: 8px;
	color: rgba(255, 247, 255, 0.9);
	font-size: 0.78rem;
	line-height: 1.4;
	text-shadow: 0 1px 6px rgba(0, 0, 0, 0.35);

	&::before {
		content: '✦';
		flex-shrink: 0;
		margin-top: 1px;
		color: ${THEME.gold};
		font-size: 0.62rem;
		text-shadow: 0 0 10px rgba(255, 211, 107, 0.4);
	}
`;

export const PlanCardsStack = styled.div`
	display: grid;
	gap: 10px;
	width: 100%;
`;

// ============================================================================
// PLAN CARDS
// ============================================================================

const selectedPlanStyles = css`
	border-color: rgba(255, 77, 242, 0.7);
	background:
		radial-gradient(circle at 100% 0%, rgba(255, 77, 242, 0.2), transparent 52%),
		rgba(255, 77, 242, 0.1);
	box-shadow:
		0 0 0 1px rgba(255, 77, 242, 0.25) inset,
		0 12px 36px rgba(176, 38, 255, 0.22),
		0 0 32px rgba(255, 77, 242, 0.14);
	transform: translateY(-2px);
`;

export const WowPlanCard = styled.button`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	box-sizing: border-box;
	width: 100%;
	height: 82px;
	min-height: 82px;
	padding: 0 16px;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 14px;
	background: rgba(8, 4, 12, 0.38);
	backdrop-filter: blur(12px);
	text-align: left;
	cursor: pointer;
	transition:
		border-color 0.22s ease,
		background 0.22s ease,
		box-shadow 0.22s ease,
		transform 0.22s ease;

	@media (max-width: 1024px) {
		width: 100%;
	}

	&:hover {
		border-color: rgba(255, 211, 107, 0.4);
		background: rgba(12, 6, 18, 0.52);
		transform: translateY(-1px);
	}

	${(props) => props.$selected && selectedPlanStyles}

	${(props) =>
		props.$popular &&
		css`
			&::before {
				content: '${props.$popularLabel || 'POPULAR'}';
				position: absolute;
				top: -9px;
				right: 14px;
				padding: 3px 10px;
				border-radius: 999px;
				background: ${THEME.gradientPrimary};
				color: #fff;
				font-size: 0.58rem;
				font-weight: 800;
				letter-spacing: 0.08em;
				box-shadow: 0 4px 16px rgba(255, 77, 242, 0.45);
			}
		`}
`;

export const PlanIcon = styled.span`
	flex-shrink: 0;
	width: 38px;
	height: 38px;
	border-radius: 12px;
	display: grid;
	place-items: center;
	font-weight: 800;
	font-size: 0.95rem;
	color: ${THEME.gold};
	background: rgba(255, 211, 107, 0.1);
	border: 1px solid rgba(255, 211, 107, 0.28);

	${(props) =>
		props.$selected &&
		css`
			background: ${THEME.gradientPrimary};
			border-color: transparent;
			color: #fff;
			box-shadow: 0 0 24px rgba(255, 77, 242, 0.35);
		`}
`;

export const PlanBody = styled.div`
	display: grid;
	gap: 3px;
	min-width: 0;
	flex: 1;
`;

export const PlanName = styled.strong`
	color: ${THEME.text};
	font-size: 0.98rem;
	font-weight: 800;
	letter-spacing: 0.04em;
	text-transform: uppercase;
`;

export const PlanMeta = styled.span`
	color: rgba(200, 170, 215, 0.9);
	font-size: 0.76rem;
	line-height: 1.35;
`;

export const PlanPrice = styled.div`
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 2px;
	color: ${THEME.gold};
	font-size: 1.08rem;
	font-weight: 800;
	white-space: nowrap;
	text-shadow: 0 0 20px rgba(255, 211, 107, 0.2);

	small {
		font-size: 0.6rem;
		font-weight: 600;
		color: rgba(200, 170, 215, 0.85);
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}
`;

// ============================================================================
// CHECKOUT PANEL (RIGHT SIDE)
// ============================================================================

export const CheckoutPanel = styled.section`
	position: relative;
	z-index: 2;
	display: flex;
	flex-direction: column;
	gap: 18px;
	min-width: 0;
	padding: 30px 28px 28px;
	background:
		radial-gradient(circle at 100% 0%, rgba(255, 77, 242, 0.1), transparent 42%),
		radial-gradient(circle at 0% 100%, rgba(255, 211, 107, 0.06), transparent 38%),
		linear-gradient(165deg, rgba(14, 7, 22, 0.98) 0%, rgba(10, 5, 16, 0.99) 100%);
	border-left: 1px solid rgba(255, 77, 242, 0.14);
	box-shadow: inset 1px 0 0 rgba(255, 255, 255, 0.05);

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 2px;
		background: linear-gradient(
			90deg,
			transparent,
			rgba(255, 77, 242, 0.6),
			rgba(255, 211, 107, 0.5),
			rgba(255, 77, 242, 0.6),
			transparent
		);
		background-size: 200% 100%;
		animation: ${shimmerLine} 4s linear infinite;
	}

	@media (max-width: 1024px) {
		padding:
			24px
			max(20px, env(safe-area-inset-right, 0px))
			calc(24px + env(safe-area-inset-bottom, 0px))
			max(20px, env(safe-area-inset-left, 0px));
		border-left: none;
		border-top: 1px solid rgba(255, 77, 242, 0.14);
	}
`;

export const CheckoutHeader = styled.header`
	margin-bottom: 4px;
`;

export const CheckoutTitle = styled.h3`
	margin: 0;
	font-family: ${THEME.fontDisplay};
	font-size: 1.28rem;
	font-weight: 800;
	line-height: 1.15;
	color: ${THEME.text};
`;

export const CheckoutFormWrap = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	min-width: 0;
	max-width: 100%;
`;

// ============================================================================
// PAYMENT STATUS & TRUST INDICATORS
// ============================================================================

export const PaymentStatusBox = styled.div`
	padding: 18px 16px;
	border-radius: 14px;
	border: 1px solid
		${(props) =>
			props.$error ? 'rgba(255, 120, 140, 0.35)' : 'rgba(255, 255, 255, 0.1)'};
	background: ${(props) =>
		props.$error ? 'rgba(255, 80, 110, 0.08)' : 'rgba(255, 255, 255, 0.04)'};
	color: ${(props) => (props.$error ? '#ff9dad' : THEME.muted)};
	font-size: 0.84rem;
	line-height: 1.45;
	text-align: center;
`;

export const TrustRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-top: 4px;
	color: rgba(164, 120, 183, 0.85);
	font-size: 0.65rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.1em;

	&::before,
	&::after {
		content: '';
		flex: 1;
		height: 1px;
		background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
	}
`;
